import { useAppDispatch, useAppSelector } from '@features';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Card, CardTitle, Label, Separator } from '@repo/shadcn';
import { FormProvider, useForm } from 'react-hook-form';
import { PiPlusCircleDuotone } from 'react-icons/pi';
import { toast } from 'sonner';
import { z } from 'zod';

import { closeModal } from '@/features/slice/event/event-slice';
import {
    useCreateExpenseCategoryMutation,
    useUpdateExpenseCategoryMutation,
} from '@/features/slice/expense/expense-api-slice';
import { DynamicControl } from '@/shared/dynamic-control';
import { DynamicFieldData } from '@/types/dynamic-control-types';

import BtnLoader from '../../../ui/loader/btn-loader';

export const createExpenseCategoryFields: DynamicFieldData[] = [
    {
        fieldName: 'name',
        inputType: 'text',
        label: 'Name',
        defaultValue: '',
        className: 'col-span-1',
    },
];

const CreateExpenseCategorySchema = z.object({
    name: z.string().min(1, 'Name is required'),
});
type CreateExpenseCategoryType = z.infer<typeof CreateExpenseCategorySchema>;

export const CreateExpenseCategory = () => {
    const [createExpenseCategory, { isLoading: isCreateLoading }] =
        useCreateExpenseCategoryMutation();
    const [updateExpenseCategory, { isLoading: isUpdateLoading }] =
        useUpdateExpenseCategoryMutation();

    const dispatch = useAppDispatch();
    const expenseCategoryData = useAppSelector(
        (state) => state.eventSlice.modalData,
    );

    const isEditMode = Boolean(expenseCategoryData?.id);
    const isLoading = isCreateLoading || isUpdateLoading;

    const form = useForm<CreateExpenseCategoryType>({
        resolver: zodResolver(CreateExpenseCategorySchema),
        defaultValues: {
            name: isEditMode ? expenseCategoryData.name : '',
        },
    });

    const { handleSubmit } = form;

    const onSubmit = async (data: CreateExpenseCategoryType) => {
        try {
            if (isEditMode) {
                await updateExpenseCategory({
                    id: expenseCategoryData.id,
                    name: data.name,
                }).unwrap();
                toast.success('Expense category updated successfully');
            } else {
                await createExpenseCategory(data);
                toast.success('Expense category created successfully');
            }

            form.reset();
            dispatch(closeModal());
        } catch (error) {
            console.error('Error creating expense category:', error);
            toast.error('Failed to create expense category');
        }
    };

    return (
        <Card className="border-none shadow-none">
            <CardTitle className="mb-2 text-[18px]">
                {' '}
                {isEditMode ? 'Edit' : 'Add'} Expense Category
            </CardTitle>
            <Separator />
            <form onSubmit={handleSubmit(onSubmit)}>
                <FormProvider {...form}>
                    <div className=" mt-5 grid grid-cols-1 gap-5">
                        {createExpenseCategoryFields.map((d, i) => (
                            <div key={i} className={`mt-3  ${d.className}`}>
                                <div className=" item-center mb-2 flex h-fit justify-between">
                                    <Label className="h-fit capitalize">
                                        {d.label}
                                    </Label>
                                    {d.actionBtn && (
                                        <Label className="flex cursor-pointer items-center  gap-1 capitalize text-primary hover:underline">
                                            <PiPlusCircleDuotone
                                                size={16}
                                                className="-mt-1"
                                            />{' '}
                                            {d.actionBtnLabel}
                                        </Label>
                                    )}
                                </div>
                                <DynamicControl {...d} />
                            </div>
                        ))}
                    </div>
                </FormProvider>

                <Button className="mt-5">
                    {isLoading ? (
                        <BtnLoader />
                    ) : isEditMode ? (
                        'Update'
                    ) : (
                        'Create'
                    )}
                </Button>
            </form>
        </Card>
    );
};

CreateExpenseCategory.displayName = 'CreateExpenseCategory';
